import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Auth0State, ShellBridge } from '@opengov/psp-contract';
import type { User } from '@auth0/auth0-react';
import {
  ShellAuth0Context,
  type ShellAuth0ContextInterface,
  type ShellRedirectOptions,
} from './context';

export interface BridgedAuth0ProviderProps {
  /** The shell bridge passed via `ModuleMountOptions.shell` in the host's
   *  `mount(container, options)` body. The provider only reads
   *  `shellBridge.auth0`; other bridge fields are ignored. */
  shellBridge: ShellBridge;
  children: ReactNode;
}

/** Minimum shell bridge version this package is compatible with. Bump this
 *  when a new release relies on bridge methods added in a later version. */
const MINIMUM_BRIDGE_VERSION = 1;

/**
 * Shell-mode internal provider. Reads `shellBridge.auth0` and pushes its
 * snapshot into `ShellAuth0Context`. Subscribes to live updates via
 * `shellBridge.auth0.onAuth0StateChange` so token refreshes, login flips, and
 * logouts reach the mounted module without a remount.
 *
 * `loginWithRedirect` is a no-op + warn — the PSP shell gates routes before
 * mount, so a remounted app sees an authenticated user.
 */
export function BridgedAuth0Provider({ shellBridge, children }: BridgedAuth0ProviderProps) {
  if (shellBridge.version < MINIMUM_BRIDGE_VERSION) {
    throw new Error(
      `BridgedAuth0Provider: shell bridge version ${shellBridge.version} is below the ` +
        `minimum required version ${MINIMUM_BRIDGE_VERSION}. Update the PSP shell to a ` +
        'version that provides a compatible bridge.'
    );
  }
  if (!shellBridge.auth0) {
    throw new Error(
      'BridgedAuth0Provider: shellBridge.auth0 is undefined. The PSP shell has not ' +
        'populated the live Auth0 surface — verify @opengov/psp-contract >= 0.4.0 and ' +
        'that apps/psp-web has finished wiring auth0Provider into createShellBridge. ' +
        'If you intended a standalone app, use <ShellAuth0Provider mode="standalone"> instead.'
    );
  }
  const auth0 = shellBridge.auth0;

  const [auth0State, setAuth0State] = useState<Auth0State>(() => auth0.getAuth0State());

  // Intentional useEffect (mount-only subscribe to an external event source).
  // This is the textbook case the React docs cite as appropriate; the project's
  // "no useEffect in app code" rule is about syncing derived state, fetching,
  // and event-handler-driven side effects — none of which apply here. The
  // returned `Dispose` from `onAuth0StateChange` becomes the cleanup.
  useEffect(() => {
    return auth0.onAuth0StateChange(setAuth0State);
  }, [auth0]);

  const value = useMemo<ShellAuth0ContextInterface>(
    () => ({
      isAuthenticated: auth0State.isAuthenticated,
      isLoading: auth0State.isLoading,
      // PspUserInfo now carries the full Auth0 User shape (via index signature
      // and spread at the bridge source), so shell-mode consumers see the same
      // fields as standalone — including picture, nickname, and custom claims.
      user: auth0State.user as User | undefined,
      error: auth0State.error,
      getAccessTokenSilently: auth0.getAccessTokenSilently,
      getIdTokenClaims: auth0.getIdTokenClaims,
      loginWithRedirect: () => {
        // TODO(logger): route this through the shell logger once available.
        console.warn(
          '[shell-auth0-react] loginWithRedirect is a no-op in shell mode — ' +
            'the PSP shell gates routes before mount.'
        );
        return Promise.resolve();
      },
      logout: (options?: ShellRedirectOptions) => {
        // shell mode: returnTo is owned by the PSP shell; ignore consumer override.
        if (options?.returnTo) {
          console.warn('[shell-auth0-react] logout returnTo is ignored in shell mode.');
        }
        return auth0.requestLogout();
      },
    }),
    [auth0State, auth0]
  );

  return <ShellAuth0Context.Provider value={value}>{children}</ShellAuth0Context.Provider>;
}
